<template>
  <div>
    <article class="flex flex-col gap-4">
      <FormGroup label="Code example" size="lg">
        <div
          class="flex flex-1 flex-row flex-wrap items-center justify-between gap-5"
        >
          <FormInputGroup fluid>
            <div class="w-10 p-2 grayscale">
              <img class="h-6" alt="" :src="langImage" />
            </div>
            <FormSelect
              class="w-72"
              v-model="lang"
              in-group
              inline
              data-id="code-samples-select"
              :option-groups="langOptions"
              size="xs"
            />
          </FormInputGroup>
          <div class="flex flex-row gap-5" v-if="selectedItem">
            <a
              class="text-link text-sm"
              v-for="item in selectedItem.links"
              :key="item.text"
              :href="item.link"
              target="_blank"
              >{{ item.text }}</a
            >
          </div>
        </div>
      </FormGroup>
      <section
        class="flex flex-col gap-2"
        v-if="codes"
        data-id="code-snippet-groups"
      >
        <div
          class="flex flex-col gap-2"
          v-for="(c, i) in codes"
          :key="i + '-code-sample'"
        >
          <p class="py-1 text-sm text-gray-700" v-if="c.text" v-html="c.text" />
          <CodeView :rounded="true" :lang="c.prism" :value="c.valueF" />
        </div>
      </section>
    </article>
  </div>
</template>
<script setup lang="ts">
import FormSelect from '../../components/form/FormSelect.vue';
import type { User, UserDto } from '@mailslurp/admin-sdk';
import { templateCodeSnippet } from '../../lib/services/helpers';
import { ref, computed, defineAsyncComponent } from 'vue';
import type { FormSelectOptionGroup } from '../../lib/services/types';
import FormGroup from '../../components/form/FormGroup.vue';
import FormInputGroup from '../../components/form/FormInputGroup.vue';
import { code, type LanguageConfig } from '../../data/shortcodes';
import Spinner from '../../components/Spinner.vue';

const langImage = computed(() => {
  switch (lang.value) {
    case 'javascript':
      return '/icons/javascript.svg';
    case 'typescript':
      return '/icons/typescript.svg';
    case 'python':
      return '/icons/python.svg';
    case 'java':
      return '/icons/java.svg';
    case 'csharp':
      return '/icons/csharp.svg';
    case 'go':
      return '/icons/go.svg';
    case 'ruby':
      return '/icons/ruby.svg';
    case 'php':
      return '/icons/php.svg';
    case 'swift':
      return '/icons/swift.svg';
    case 'kotlin':
      return '/icons/kotlin.svg';
    case 'rust':
      return '/icons/rust.svg';
    case 'scala':
      return '/icons/scala.svg';
    case 'r':
      return '/icons/r.svg';
    case 'shell':
      return '/icons/shell.svg';
    case 'powershell':
      return '/icons/powershell.svg';
    default:
      return '/docs/restapi.svg';
  }
});
const CodeView = defineAsyncComponent({
  loader: () => import('../../components/form/CodeView.vue'),
  loadingComponent: Spinner,
});

/**
 * For viewing code samples in different languages etc
 */
const props = withDefaults(
  defineProps<{
    user?: User | UserDto | null;
  }>(),
  {
    user: null,
  },
);
const lang = ref(code.defaultLanguage);
const langOptions: FormSelectOptionGroup[] = code.items.map((item) => {
  return {
    text: item.group,
    options: item.examples.map((l) => ({
      text: l.label,
      value: l.language,
    })),
  };
});
const selectedItem = computed<LanguageConfig | undefined>(() => {
  return code.items
    .map((g) => g.examples)
    .flat()
    .find((o) => o.language === lang.value);
});
const codes = computed(() => {
  return selectedItem.value?.code.map((c) => ({
    prism: selectedItem.value?.prism,
    text: c.text,
    valueF: templateCodeSnippet(
      c.source,
      {
        user: props.user ?? undefined,
      },
      c?.quote ?? true,
    ),
  }));
});
</script>
